import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { STAGE_LABELS, SOURCE_LABELS } from "../lib/constants";
import { LevelBadge } from "../components/LevelBadge";
import { AssignInstallersDialog } from "../components/AssignInstallersDialog";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { HardHat, Loader2, Search, UserPlus, History, ChevronDown, ChevronUp } from "lucide-react";

const STAGE_ORDER = ["lead", "profilazione", "consulenza", "preventivo", "contratto"];
const STAGE_COLORS = { lead: "#9CA3AF", profilazione: "#60A5FA", consulenza: "#A78BFA", preventivo: "#F59E0B", contratto: "#34D399" };
const BOARD_ST = { attivo: ["Attivo", "#34D399"], parcheggio: ["Parcheggio", "#A855F7"], cimitero: ["Cimitero", "#9CA3AF"] };
const fmtDT = (v) => (v ? new Date(v).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

function UpdatesFeed({ leads, onOpen }) {
  const [open, setOpen] = useState(true);
  const feed = useMemo(() => (leads || []).flatMap((l) => (l.updates || []).map((u) => ({ ...u, lead: l })))
    .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))).slice(0, 40), [leads]);
  return (
    <Card className="p-4" data-testid="board-updates-feed">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between" data-testid="board-updates-toggle">
        <span className="text-sm font-semibold flex items-center gap-2"><History className="h-4 w-4 text-primary" />Aggiornamenti di stato dei lead in gara</span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <div className="mt-3 max-h-80 overflow-y-auto divide-y divide-border">
          {feed.length === 0 && <p className="text-xs text-muted-foreground py-3">Nessun aggiornamento.</p>}
          {feed.map((u, i) => (
            <button key={i} type="button" onClick={() => onOpen(u.lead.id)} className="w-full text-left py-2 hover:bg-secondary/40 px-1 flex gap-3" data-testid={`board-update-${i}`}>
              <span className="text-[11px] text-muted-foreground w-28 shrink-0">{fmtDT(u.created_at)}</span>
              <span className="min-w-0 text-xs">
                <b>{u.lead.name}</b> <span className="text-muted-foreground">· {u.type}{u.by ? ` · ${u.by}` : ""}</span>
                <span className="block text-foreground/80 truncate">{u.note}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

const ST_COLORS = { proposto: "#F59E0B", accettato: "#60A5FA", rifiutato: "#EF4444", selezionato: "#34D399", non_selezionato: "#9CA3AF" };

const EMAIL_STATE = {
  sent: { color: "#34D399", label: "Email di attivazione inviata" },
  queued: { color: "#F59E0B", label: "Email di attivazione in coda di invio" },
  failed: { color: "#EF4444", label: "Email di attivazione NON recapitata" },
  no_email: { color: "#71717A", label: "Email mancante in anagrafica — installatore non contattato" },
  unknown: { color: "#3F3F46", label: "Stato email non disponibile" },
};

function EmailDot({ info }) {
  const st = EMAIL_STATE[info?.state || "unknown"] || EMAIL_STATE.unknown;
  const title = info?.error ? `${st.label}: ${info.error}` : st.label;
  return (
    <span
      data-testid="installer-email-dot"
      title={title}
      className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
      style={{ backgroundColor: st.color }}
    />
  );
}

function LegendItem({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

export default function HeadPartnerBoard() {
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");
  const [stFilter, setStFilter] = useState("attivo");
  const [dialogLead, setDialogLead] = useState(null);
  const [emailStatus, setEmailStatus] = useState({});
  const navigate = useNavigate();

  const fetchLeads = useCallback(() => {
    api.get("/bacheca/leads")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setLeads(list);
        const ids = list.map((l) => l.id).filter(Boolean);
        if (ids.length) {
          api.post("/leads/installers-email-status", { lead_ids: ids })
            .then((res) => setEmailStatus(res.data || {}))
            .catch(() => setEmailStatus({}));
        } else {
          setEmailStatus({});
        }
      })
      .catch(() => setLeads([]));
  }, []);
  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = (leads || []).filter((l) => stFilter === "tutti" || l.board_status === stFilter);
    if (!s) return list;
    return list.filter((l) => [l.name, l.city, l.email, l.phone, l.assigned_to_name].filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [leads, q, stFilter]);

  const byStage = useMemo(() => {
    const g = {};
    for (const l of filtered) (g[l.stage] = g[l.stage] || []).push(l);
    return g;
  }, [filtered]);

  return (
    <div className="space-y-6" data-testid="head-partner-board">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
            <HardHat className="h-6 w-6 text-primary" strokeWidth={1.5} /> Bacheca per installatori
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Lead assegnati all'<b>Head of Partner</b> o con <b>gara installatori attiva</b>, compresi parcheggiati e cimitero.
          </p>
        </div>
        <div className="relative max-w-xs w-full">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca lead o installatore…" className="pl-9" data-testid="board-search" />
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="board-status-filter">
        {[["attivo", "Attivi"], ["parcheggio", "Parcheggiati"], ["cimitero", "Cimitero"], ["tutti", "Tutti"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setStFilter(k)} data-testid={`board-filter-${k}`}
            className={`text-xs rounded-full border px-3 py-1 transition-colors ${stFilter === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
            {l} <b>{k === "tutti" ? (leads || []).length : (leads || []).filter((x) => x.board_status === k).length}</b>
          </button>
        ))}
      </div>

      <UpdatesFeed leads={leads} onOpen={(id) => navigate(`/leads/${id}`)} />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="board-stats">
        <Card className="p-4">
          <div className="text-2xl font-bold text-primary">{filtered.length}</div>
          <div className="text-xs text-muted-foreground mt-1">Lead in bacheca</div>
        </Card>
        {STAGE_ORDER.map((st) => (
          <Card key={st} className="p-4">
            <div className="text-2xl font-bold" style={{ color: STAGE_COLORS[st] }}>{(byStage[st] || []).length}</div>
            <div className="text-xs text-muted-foreground mt-1">{STAGE_LABELS[st] || st}</div>
          </Card>
        ))}
      </div>

      {leads === null ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="board-empty">
          Nessun lead assegnato agli installatori al momento.
        </Card>
      ) : (
        <>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground" data-testid="email-status-legend">
          <span className="font-medium text-foreground/70">Stato email attivazione:</span>
          <LegendItem color="#34D399" label="Inviata" />
          <LegendItem color="#F59E0B" label="In coda" />
          <LegendItem color="#EF4444" label="Non recapitata" />
          <LegendItem color="#71717A" label="Email mancante" />
        </div>
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Referente</TableHead>
                <TableHead>Fase</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Ultimo aggiornamento</TableHead>
                <TableHead>Livello</TableHead>
                <TableHead>Installatori in gara</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((l) => {
                const ai = l.assigned_installers || [];
                return (
                  <TableRow key={l.id} className="border-border" data-testid={`board-row-${l.id}`}>
                    <TableCell className="font-medium cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)}>{l.name}</TableCell>
                    <TableCell className="text-sm cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)}>{l.assigned_to_name || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal text-[11px]" style={{ borderColor: (STAGE_COLORS[l.stage] || "#9CA3AF") + "66", color: STAGE_COLORS[l.stage] || "#9CA3AF" }}>
                        {STAGE_LABELS[l.stage] || l.stage}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal text-[11px]" style={{ borderColor: BOARD_ST[l.board_status]?.[1] + "66", color: BOARD_ST[l.board_status]?.[1] }} data-testid={`board-status-${l.id}`}>{BOARD_ST[l.board_status]?.[0]}</Badge>
                    </TableCell>
                    <TableCell className="text-xs max-w-[240px]">
                      {l.updates?.[0] ? <><div className="text-muted-foreground">{fmtDT(l.updates[0].created_at)}</div><div className="truncate" title={l.updates[0].note}>{l.updates[0].note}</div></> : "—"}
                    </TableCell>
                    <TableCell>{l.level ? <LevelBadge level={l.level} /> : <span className="text-muted-foreground text-sm">—</span>}</TableCell>
                    <TableCell>
                      {ai.length === 0 ? (
                        <span className="text-xs text-muted-foreground">Nessuno</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 max-w-[280px]">
                          {ai.slice(0, 4).map((i) => (
                            <Badge key={i.installer_id} variant="outline" className="text-[10px] font-normal inline-flex items-center gap-1" style={{ borderColor: (ST_COLORS[i.status] || "#9CA3AF") + "66", color: ST_COLORS[i.status] || "#9CA3AF" }} title={i.status}>
                              <EmailDot info={emailStatus[`${l.id}:${i.installer_id}`]} />
                              {i.ragione_sociale}
                            </Badge>
                          ))}
                          {ai.length > 4 && <Badge variant="secondary" className="text-[10px] font-normal">+{ai.length - 4}</Badge>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setDialogLead(l)} data-testid={`assign-installers-btn-${l.id}`}>
                        <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Installatori
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
        </>
      )}

      <AssignInstallersDialog
        lead={dialogLead}
        open={!!dialogLead}
        onOpenChange={(v) => { if (!v) setDialogLead(null); }}
        onChanged={fetchLeads}
      />
    </div>
  );
}
